import { Dialog, DialogContent } from '@material-ui/core';
import React, { JSX, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IInventoryRule } from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { formatDataIntoLVTRows } from '~/src/dash/components/tables/IDNameTable';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extraSmallTableRowsPerPageOptions,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
  TKey,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { LoadingStateHasData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { inventoryRulesTableModalConfig } from '~/src/dash/views/inventory-rules-v2/InventoryRulesModal/config';

interface IInventoryRulesModalProps {
  open: boolean;
  onClose: () => void;
  inventoryRules: IInventoryRule[] | null;
  descriptionTKey: TKey;
}

export const InventoryRulesModal = ({
  open,
  onClose,
  inventoryRules,
  descriptionTKey,
}: IInventoryRulesModalProps): JSX.Element => {
  const { isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const modalCfg = inventoryRulesTableModalConfig;

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>(modalCfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string>((modalCfg.defaultState.orderBy as string) || 'ID');
  const [rowsPerPage, setRowsPerPage] = useState(modalCfg.defaultState.rowsPerPage || 10);

  const handleClose = (): void => {
    setSearchQuery('');
    setPage(0);
    onClose();
  };

  const handleSearchChange = (text: string): void => {
    unstable_batchedUpdates(() => {
      setSearchQuery(text);
      setPage(0);
    });
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    unstable_batchedUpdates(() => {
      setRowsPerPage(parseInt(event.target.value, 10) as RowsPerPage);
      setPage(0);
    });
  };

  const handleClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const filteredRules = useMemo(() => {
    const rules = inventoryRules || [];
    if (!searchQuery) return rules;
    const lowerQuery = searchQuery.toLowerCase();
    return rules.filter((rule) => {
      const nameMatch = rule.Name?.toLowerCase().includes(lowerQuery);
      const idMatch = rule.ID?.toString().includes(lowerQuery);
      return nameMatch || idMatch;
    });
  }, [inventoryRules, searchQuery]);

  const rows = formatDataIntoLVTRows(filteredRules, modalCfg.columns).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Dialog fullWidth maxWidth='md' keepMounted open={open} onClose={handleClose}>
      <DialogContent style={{ padding: spacing.large, maxHeight: '100%' }}>
        <Rows style={{ gap: spacing.medium }}>
          <Row noPadding>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Col>
                <Rows style={{ gap: spacing.extraSmall }}>
                  <Row noPadding>
                    <Text large bold tKey='InventoryRules' />
                  </Row>
                  <Row noPadding>
                    <Text small muted tKey={descriptionTKey} />
                  </Row>
                </Rows>
              </Col>
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleSearchChange}
                  initialValue={searchQuery}
                  syncWithParent
                  msDelay={300}
                  placeholder={t('SearchByNameID')}
                  prefix='search'
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <ListViewTable
              activeColumnKeys={[]}
              columns={modalCfg.columns}
              displayNameWithID
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={LoadingStateHasData}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={extraSmallTableRowsPerPageOptions}
              totalRowCount={filteredRules.length}
              className={modalCfg.cypressClassName}
            />
          </Row>
        </Rows>
      </DialogContent>
    </Dialog>
  );
};
